import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  Conversa,
  ConversaDetalhe,
  HistoricoMensagem,
  MetricasIAWhatsApp,
  LeadParaVincular
} from '@/types';

interface ConversasResponse {
  success: boolean;
  data: {
    conversas: Conversa[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

interface ConversaResponse {
  success: boolean;
  data: { conversa: ConversaDetalhe };
}

interface HistoricoResponse {
  success: boolean;
  data: { historico: HistoricoMensagem[] };
}

interface MetricasResponse {
  success: boolean;
  data: { metricas: MetricasIAWhatsApp };
}

interface ToggleIAResponse {
  success: boolean;
  data: {
    conversa: {
      channel_id: string;
      is_ai_disabled: boolean;
      disabled_since: string | null;
    };
    message: string;
  };
}

interface LeadsDisponiveisResponse {
  success: boolean;
  data: { leads: LeadParaVincular[] };
}

interface GlobalControlResponse {
  success: boolean;
  data: {
    is_disabled: boolean;
    disabled_since: string | null;
    updated_at: string | null;
    n8n_workflow_active: boolean | null;
  };
}

interface CreateLeadResponse {
  success: boolean;
  data: {
    lead: {
      id: number;
      nome: string;
      email: string | null;
      telefone: string | null;
    };
  };
}

// Query keys
const CONVERSAS_KEY = 'ia-whatsapp-conversas';
const CONVERSA_KEY = 'ia-whatsapp-conversa';
const HISTORICO_KEY = 'ia-whatsapp-historico';
const METRICAS_KEY = 'ia-whatsapp-metricas';
const LEADS_DISPONIVEIS_KEY = 'ia-whatsapp-leads-disponiveis';
const GLOBAL_CONTROL_KEY = 'ia-whatsapp-global-control';

export function useConversas(params?: { search?: string; status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [CONVERSAS_KEY, params],
    queryFn: async () => {
      const response = await api.get<ConversasResponse>('/ia-whatsapp/conversas', {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.status ? { status: params.status } : {})
        }
      });
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useConversa(channelId: string | null) {
  return useQuery({
    queryKey: [CONVERSA_KEY, channelId],
    queryFn: async () => {
      if (!channelId) return null;
      const response = await api.get<ConversaResponse>(`/ia-whatsapp/conversas/${channelId}`);
      return response.data.conversa;
    },
    enabled: !!channelId,
  });
}

export function useHistorico(channelId: string | null, limit = 50) {
  return useQuery({
    queryKey: [HISTORICO_KEY, channelId, limit],
    queryFn: async () => {
      if (!channelId) return [];
      const response = await api.get<HistoricoResponse>(`/ia-whatsapp/conversas/${channelId}/historico`, {
        params: { limit }
      });
      return response.data.historico;
    },
    enabled: !!channelId,
  });
}

export function useMetricas() {
  return useQuery({
    queryKey: [METRICAS_KEY],
    queryFn: async () => {
      const response = await api.get<MetricasResponse>('/ia-whatsapp/metricas');
      return response.data.metricas;
    },
    refetchInterval: 30000,
  });
}

export function useToggleIA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channelId, is_ai_disabled }: { channelId: string; is_ai_disabled: boolean }) => {
      const response = await api.patch<ToggleIAResponse>(`/ia-whatsapp/conversas/${channelId}/toggle-ia`, {
        is_ai_disabled
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [CONVERSAS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONVERSA_KEY, variables.channelId] });
      queryClient.invalidateQueries({ queryKey: [METRICAS_KEY] });
    },
  });
}

export function useVincularLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channelId, leadId }: { channelId: string; leadId: number }) => {
      const response = await api.post<{ success: boolean; data: unknown }>(`/ia-whatsapp/conversas/${channelId}/vincular-lead`, {
        lead_id: leadId
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONVERSAS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONVERSA_KEY, variables.channelId] });
    },
  });
}

export function useDesvincularLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channelId, leadId }: { channelId: string; leadId: number }) => {
      const response = await api.delete<{ success: boolean; data: unknown }>(`/ia-whatsapp/conversas/${channelId}/vincular-lead`, {
        data: { lead_id: leadId }
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONVERSAS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONVERSA_KEY, variables.channelId] });
    },
  });
}

export function useLeadsDisponiveis(search?: string) {
  return useQuery({
    queryKey: [LEADS_DISPONIVEIS_KEY, search],
    queryFn: async () => {
      const response = await api.get<LeadsDisponiveisResponse>('/ia-whatsapp/leads-disponiveis', {
        params: search ? { search } : undefined
      });
      return response.data.leads;
    },
    enabled: search === undefined || search.length >= 2,
  });
}

export function useCriarLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { nome: string; email?: string; telefone?: string; empresa?: string; cargo?: string; fonte?: string }) => {
      const response = await api.post<CreateLeadResponse>('/leads', payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: [LEADS_DISPONIVEIS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONVERSAS_KEY] });
    },
  });
}

export function useGlobalIAControl() {
  return useQuery({
    queryKey: [GLOBAL_CONTROL_KEY],
    queryFn: async () => {
      const response = await api.get<GlobalControlResponse>('/ia-whatsapp/global-control');
      return response.data;
    },
    refetchInterval: 15000, // Refetch every 15 seconds
  });
}

export function useSetGlobalIAControl() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ is_disabled }: { is_disabled: boolean }) => {
      const response = await api.post<GlobalControlResponse>('/ia-whatsapp/global-control', {
        is_disabled
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GLOBAL_CONTROL_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONVERSAS_KEY] });
      queryClient.invalidateQueries({ queryKey: [METRICAS_KEY] });
    },
  });
}
